<template>
  <el-select
    class="seletor-busca"
    :value="value"
    filterable
    :remote="remoto"
    :remote-method="remoto ? aoDigitar : undefined"
    :filter-method="remoto ? undefined : filtrarLocal"
    :loading="carregando && !opcoesExibidas.length"
    :clearable="clearable"
    :default-first-option="livre || remoto"
    :disabled="disabled"
    :placeholder="placeholder"
    :no-match-text="textoExibido"
    :no-data-text="textoExibido"
    loading-text="Buscando..."
    :size="size"
    :aria-label="ariaLabel || placeholder"
    @input="aoSelecionar"
    @visible-change="aoAbrir"
    @clear="aoLimpar"
  >
    <template v-if="$scopedSlots.prefixo && opcaoAtual" #prefix>
      <span class="seletor-busca__prefixo">
        <slot name="prefixo" :opcao="opcaoAtual" />
      </span>
    </template>
    <el-option
      v-for="opcao in opcoesExibidas"
      :key="chaveDa(opcao)"
      :label="rotuloDa(opcao)"
      :value="opcao.id"
      :class="{ 'seletor-busca__item--rico': !!$scopedSlots.opcao }"
    >
      <slot name="opcao" :opcao="opcao">
        <span class="seletor-busca__item">
          <strong v-if="exibeCodigo(opcao)" class="seletor-busca__codigo">{{ opcao.id }}</strong>
          <span v-if="exibeCodigo(opcao)" class="seletor-busca__separador">-</span>
          <span class="seletor-busca__descricao">{{ opcao.descricao || opcao.id }}</span>
        </span>
      </slot>
    </el-option>
    <el-option
      v-if="opcaoLivre"
      :key="`seletor-busca-livre-${opcaoLivre.id}`"
      :label="opcaoLivre.id"
      :value="opcaoLivre.id"
    >
      <span class="seletor-busca__item">
        <span class="seletor-busca__descricao">Usar o código <strong>{{ opcaoLivre.id }}</strong></span>
      </span>
    </el-option>
    <el-option
      v-if="avisoErroRemoto"
      key="seletor-busca-erro"
      :value="VALOR_AVISO_ERRO"
      :label="textoExibido"
      disabled
      class="seletor-busca__aviso"
    />
  </el-select>
</template>

<script>
import _debounce from "lodash/debounce";
import { get } from "@/helpers/apiRequest";
import { normalizeString } from "@/helpers/utils";

const DEBOUNCE_BUSCA_MS = 300;
const TEXTO_ERRO_CARGA = "Não foi possível carregar as opções";
const VALOR_AVISO_ERRO = "__seletor_busca_erro__";

const mapearPadrao = (item) => ({
  id: item.id,
  descricao: item.descricao || item.desc || item.label || "",
});

const normalizar = (texto) => normalizeString(String(texto === null || texto === undefined ? "" : texto)).toLowerCase();

export default {
  name: "SeletorBusca",

  props: {
    value: { default: null },
    opcoes: { type: Array, default: () => [] },
    url: { type: String, default: "" },
    mapear: { type: Function, default: mapearPadrao },
    rotuloAtual: { type: [String, Number], default: "" },
    ocultar: { type: Array, default: () => [] },
    placeholder: { type: String, default: "Selecione" },
    ariaLabel: { type: String, default: "" },
    textoVazio: { type: String, default: "Nenhum resultado encontrado" },
    mostrarCodigo: { type: Boolean, default: true },
    clearable: { type: Boolean, default: true },
    livre: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    erro: { type: Boolean, default: false },
    limite: { type: Number, default: 100 },
    size: { type: String, default: "" },
  },

  data() {
    return {
      filtro: "",
      digitado: "",
      remotas: [],
      termoRemoto: null,
      termoBuscando: null,
      carregando: false,
      erroBusca: false,
      selecionada: null,
      VALOR_AVISO_ERRO,
    };
  },

  computed: {
    remoto() {
      return !!this.url;
    },

    locaisFiltradas() {
      const alvo = normalizar(this.filtro);
      const lista = alvo
        ? this.opcoes.filter((opcao) => normalizar(`${opcao.id} ${opcao.descricao || ""} ${opcao.busca || ""}`).includes(alvo))
        : this.opcoes;

      return lista.slice(0, this.limite);
    },

    textoExibido() {
      return this.erro || (this.remoto && this.erroBusca) ? TEXTO_ERRO_CARGA : this.textoVazio;
    },

    temValor() {
      return this.value !== null && this.value !== undefined && this.value !== "";
    },

    opcaoAtual() {
      if (!this.temValor) return null;

      const conhecidas = [...this.opcoes, ...this.remotas, ...(this.selecionada ? [this.selecionada] : [])];
      const exata = conhecidas.find((opcao) => opcao.id === this.value);
      const parecida = exata || conhecidas.find((opcao) => String(opcao.id) === String(this.value));
      const descricao = (parecida && parecida.descricao) || (this.rotuloAtual !== "" ? String(this.rotuloAtual) : "");

      return { ...(parecida || {}), id: this.value, descricao };
    },

    avisoErroRemoto() {
      return this.remoto && this.erroBusca && !this.carregando && !this.opcoesExibidas.length;
    },

    textoDigitado() {
      return String(this.digitado || "").trim();
    },

    buscaConcluida() {
      if (!this.remoto) return true;

      return !this.carregando && this.termoBuscando === null && this.termoRemoto === this.digitado;
    },

    opcaoLivre() {
      const texto = this.textoDigitado;

      if (!this.livre || !texto || !this.buscaConcluida) return null;

      const base = this.remoto ? this.remotas : this.locaisFiltradas;

      if (base.length && !(this.remoto && this.erroBusca)) return null;

      return { id: texto, descricao: "" };
    },

    opcoesExibidas() {
      const ocultas = this.ocultar.map(String);
      const todas = this.remoto ? this.remotas : this.locaisFiltradas;
      const base = ocultas.length ? todas.filter((opcao) => !ocultas.includes(String(opcao.id))) : todas;

      if (!this.opcaoAtual || base.some((opcao) => opcao.id === this.value)) return base;

      if (this.textoDigitado) return base.filter((opcao) => String(opcao.id) !== String(this.value));

      return [this.opcaoAtual, ...base.filter((opcao) => String(opcao.id) !== String(this.value))];
    },
  },

  created() {
    this.buscarComDebounce = _debounce(this.buscar, DEBOUNCE_BUSCA_MS);
  },

  mounted() {
    if (this.remoto && !this.disabled) this.buscar("");
  },

  beforeDestroy() {
    this.buscarComDebounce.cancel();
  },

  methods: {
    chaveDa(opcao) {
      return `${typeof opcao.id}-${opcao.id}`;
    },

    exibeCodigo(opcao) {
      return this.mostrarCodigo && !!opcao.descricao && String(opcao.descricao) !== String(opcao.id);
    },

    rotuloDa(opcao) {
      if (this.exibeCodigo(opcao)) return `${opcao.id} - ${opcao.descricao}`;

      return String(opcao.descricao || opcao.id);
    },

    filtrarLocal(termo) {
      this.filtro = termo || "";
      this.digitado = this.filtro;
    },

    aoDigitar(termo) {
      this.digitado = String(termo || "");
      this.buscarComDebounce(termo);
    },

    aoAbrir(aberto) {
      this.digitado = "";

      if (!aberto) return;

      this.$emit("abrir");

      if (!this.remoto) {
        this.filtro = "";

        return;
      }

      this.buscar("");
    },

    aoLimpar() {
      if (this.remoto) this.buscar("");
    },

    opcaoDigitada(valor) {
      const exata = this.opcoesExibidas.find((item) => item.id === valor);

      if (exata || !this.livre || typeof valor !== "string") return exata || null;

      const texto = valor.trim();
      const alvo = normalizar(texto);

      return (
        this.opcoesExibidas.find((item) => String(item.id) === texto) ||
        this.opcoesExibidas.find((item) => !!item.descricao && normalizar(item.descricao) === alvo) ||
        null
      );
    },

    aoSelecionar(entrada) {
      if (entrada === VALOR_AVISO_ERRO) return;

      const conhecida = this.opcaoDigitada(entrada);
      const digitado = this.livre && typeof entrada === "string" ? entrada.trim() : entrada;
      const valor = conhecida ? conhecida.id : digitado;
      const vazio = valor === "" || valor === null || valor === undefined;
      const opcao = vazio ? null : conhecida || { id: valor, descricao: "" };

      this.selecionada = opcao;
      this.$emit("input", vazio ? null : valor);
      this.$emit("change", vazio ? null : valor, opcao);
    },

    buscar(termo) {
      const consulta = String(termo || "");

      if (consulta === this.termoBuscando || (consulta === this.termoRemoto && this.remotas.length && !this.carregando)) return;

      this.termoBuscando = consulta;

      const separador = this.url.includes("?") ? "&" : "?";
      const requisicao = (this.requisicao = (this.requisicao || 0) + 1);

      this.carregando = true;

      return get(`${this.url}${separador}page=1&q=${encodeURIComponent(consulta)}`)
        .then((json) => {
          if (requisicao !== this.requisicao) return;

          const itens = (json && (json.itens || json.data)) || (Array.isArray(json) ? json : []);

          this.termoBuscando = null;
          this.erroBusca = false;
          this.remotas = itens.map(this.mapear).filter((opcao) => opcao && opcao.id !== null && opcao.id !== undefined);
          this.termoRemoto = consulta;
        })
        .catch(() => {
          if (requisicao !== this.requisicao) return;

          this.termoBuscando = null;
          this.erroBusca = true;
          this.remotas = [];
          this.termoRemoto = consulta;
        })
        .finally(() => {
          if (requisicao === this.requisicao) this.carregando = false;
        });
    },
  },
};
</script>

<style scoped>
.seletor-busca {
  width: 100%;
}

.seletor-busca__prefixo {
  display: inline-flex;
  align-items: center;
  height: 100%;
  padding-left: 4px;
  color: var(--cashback-cor-primaria, #3a3e81);
}

.seletor-busca__item {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
}

.seletor-busca__codigo {
  color: #344054;
}

.seletor-busca__separador {
  color: #98a2b3;
}

.seletor-busca__descricao {
  overflow: hidden;
  text-overflow: ellipsis;
}

.seletor-busca__aviso.el-select-dropdown__item.is-disabled {
  color: #b42318;
  cursor: default;
  white-space: normal;
}

.seletor-busca__item--rico.el-select-dropdown__item {
  height: auto;
  padding-top: 6px;
  padding-bottom: 6px;
  line-height: 1.35;
  white-space: normal;
}
</style>
